import { capitalize, debounce, isNil, omitBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import useLocalStorage from 'use-local-storage';
import usePrevious from './use-previous';
import deepEqual from 'deep-equal';

const STATIC_EXCLUDE_KEYS_TO_COUNT = ['pageNumber', 'pageSize', 'sortBy', 'sortDirection'];

function extractPathname(path: string) {
  return path.includes('manager/') ? path.split('manager/')[1] || '' : path;
}

export function useRequestQueryParams<T extends object>(
  initialRequest: T,
  callback: (request: T) => void | Promise<void>,
  callbackDeps: any[] = [],
  excludeKeysToCount: string[] = [],
  customDebounceTime: number = 500,
) {
  const { pathname } = useLocation();
  const [request, setRequest] = useState<T>(initialRequest);
  const [numOfParams, setNumOfParams] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [, setLocalSearchParams] = useLocalStorage<Record<string, any>>(`search_${extractPathname(pathname)}`, {});
  const preCallbackDeps = usePrevious(callbackDeps);
  const preRequest = usePrevious(request);

  // Parse property
  const parsePropPrefix = (key: string, prefix?: string) => (prefix ? `${prefix}${capitalize(key)}` : key);

  // Parse request to params
  const parseRequestToParams = (t: T, prefix?: string): [string, any][] => {
    const object = omitBy(t, isNil);

    return Object.keys(object).flatMap((key) => {
      const value = object[key];

      if (object[key].constructor === Object) {
        return parseRequestToParams(value, parsePropPrefix(key, prefix));
      }

      return [[parsePropPrefix(key, prefix), value]];
    });
  };

  // Set the number of params
  const countActiveParams = (parsedParams: [string, any][]) => {
    const params = parsedParams.filter(
      ([key]) => !excludeKeysToCount.includes(key) && !STATIC_EXCLUDE_KEYS_TO_COUNT.includes(key),
    );
    setNumOfParams(params.length);
  };

  // Debounce callback to prevent multiple requests
  // This should be called from any component only when the rows change their properties (like status, name, etc)
  const fetchCallback = useCallback(
    debounce(async () => {
      if (!deepEqual(request, preRequest) || !deepEqual(callbackDeps, preCallbackDeps)) {
        callback(request);
      }
    }, customDebounceTime),
    [request, ...callbackDeps],
  );

  // Run the debounce callback when the search params change
  useEffect(() => {
    fetchCallback();
  }, [searchParams, ...callbackDeps]);

  // Update the search params and store them when the request changes
  useEffect(() => {
    const newQueryParams = parseRequestToParams(request);

    countActiveParams(newQueryParams);
    setLocalSearchParams(newQueryParams);
    setSearchParams(new URLSearchParams(newQueryParams));

    // TODO: remove this when order list (or any list 2.0 inside an iframe) is removed
    if (window.self !== window.parent) {
      window.parent.postMessage({ type: 'legacy-view', action: 'updateSearchParams', data: newQueryParams }, '*');
    }
  }, [request]);

  return { request, setRequest, fetchCallback: () => callback(request), numOfParams };
}

export default useRequestQueryParams;
